import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import { toast } from 'react-toastify';
import useEmailMarketing from '../../../hooks/useEmailMarketing';

const OUTCOME_LABEL = {
  ran: 'Durchgelaufen',
  notFound: 'Kein Kontakt',
  unsubscribed: 'Abgemeldet',
  notEnrolled: 'Nicht eingeschrieben',
};

const parseEmails = raw => [
  ...new Set(
    String(raw || '')
      .split(/[\s,;]+/)
      .map(entry => entry.trim().toLowerCase())
      .filter(Boolean)
  ),
];

/**
 * Staging-only: runs the whole flow for a handful of addresses right now, with
 * every wait collapsed. The caller decides whether to render it at all
 * (`isStagingBuild()`); the endpoint refuses outside staging regardless.
 */
export default function TestRunDialog({ open, onClose, flowId, flowName, suggestedEmails = [] }) {
  const { testRunFlow } = useEmailMarketing();
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailCount, setEmailCount] = useState(suggestedEmails.length);
  const inputRef = useRef(null);

  const initialValue = useMemo(() => suggestedEmails.join('\n'), [suggestedEmails]);

  const handleRun = async () => {
    const emails = parseEmails(inputRef.current?.value);
    if (!emails.length) {
      toast.error('Bitte mindestens eine E-Mail-Adresse eintragen.');
      return;
    }
    if (emails.length > 25) {
      toast.error('Höchstens 25 Adressen pro Testlauf.');
      return;
    }
    setBusy(true);
    try {
      const response = await testRunFlow(flowId, emails);
      const list = response?.results || [];
      setResults(list);
      const ran = list.filter(entry => entry.outcome === 'ran').length;
      toast.success(`Testlauf abgeschlossen — ${ran} von ${list.length} durchgelaufen.`);
    } catch (e) {
      toast.error('Der Testlauf ist fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setResults(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Sofort-Testlauf: {flowName}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Spielt den kompletten Flow für die genannten Adressen jetzt durch — alle Wartezeiten
          übersprungen.
        </DialogContentText>

        <Alert severity='warning' sx={{ mb: 3 }}>
          <AlertTitle>Das ist keine Trockenübung</AlertTitle>
          Gutscheine werden wirklich angelegt, GetResponse bekommt wirklich die Felder und Tags, und
          die Mails gehen an die genannten Adressen wirklich raus. Nur auf Staging möglich.
        </Alert>

        <TextField
          inputRef={inputRef}
          label='E-Mail-Adressen (eine pro Zeile oder per Komma getrennt)'
          defaultValue={initialValue}
          placeholder='max@example.com'
          multiline
          rows={4}
          fullWidth
          size='small'
          onChange={event => setEmailCount(parseEmails(event.target.value).length)}
          helperText={`${emailCount} ${
            emailCount === 1 ? 'Adresse' : 'Adressen'
          } · höchstens 25 · die Kontakte müssen bereits existieren`}
        />

        {results && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
            {results.map(result => (
              <Box key={result.email} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {result.email}
                  </Typography>
                  <Chip
                    size='small'
                    color={result.outcome === 'ran' ? 'success' : 'warning'}
                    label={OUTCOME_LABEL[result.outcome] || result.outcome}
                  />
                </Box>

                {result.note && (
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                    {result.note}
                  </Typography>
                )}

                {result.outcome === 'ran' && (
                  <>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {result.actions || 0} Aktionen
                      {result.skipped ? ` · ${result.skipped} übersprungen` : ''}
                      {result.failed ? ` · ${result.failed} fehlgeschlagen` : ''}
                      {result.restarted ? ' · laufende Einschreibung ersetzt' : ''}
                      {result.reachedFlowEnd ? ' · bis zum Ende' : ''}
                      {result.cappedAtMaxHops ? ' · abgebrochen (zu viele Schritte)' : ''}
                    </Typography>
                    {result.history?.length > 0 && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.75rem', opacity: 0.7 }}>
                          Verlauf ({result.history.length} Schritte)
                        </summary>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5 }}>
                          {result.history.map((entry, index) => (
                            <Typography
                              key={`${entry.blockId}-${index}`}
                              variant='caption'
                              color='text.secondary'
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {entry.blockId} · {entry.action}
                              {entry.note ? ` — ${entry.note}` : ''}
                            </Typography>
                          ))}
                        </Box>
                      </details>
                    )}
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
        <Button
          variant='contained'
          onClick={handleRun}
          disabled={busy || emailCount === 0}
          startIcon={busy ? <CircularProgress size={16} color='inherit' /> : <ScienceIcon />}
        >
          {results ? 'Erneut ausführen' : 'Jetzt testen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
